import { t } from "@/lib/i18n/interpolate";
import {
  familyName,
  mlaAuthors,
  type PersonName,
  parsePersonName,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import type {
  DocBlock,
  DocField,
  DocRun,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  interpolateRuns,
  leadInItem,
  listOrPrompt,
  paragraphsOrPrompt,
  splitItems,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A book report at three school levels. The facts a reader enters are the same at every
 * level; what changes is the structure built around them — friendly fill-in sections in
 * elementary school, a five-part report in middle school, and an analytical report with
 * a thesis, textual evidence and an MLA 9 Work Cited entry in high school.
 */

export type BookReportLevel = "elementary" | "middle" | "high";

export interface BookReportInput {
  level: BookReportLevel;
  student: string;
  title: string;
  author: string;
  genre: string;
  year: string;
  publisher: string;
  setting: string;
  /** One per line: "Wilbur — a young pig who worries a lot". */
  characters: string;
  plot: string;
  /** One per line. */
  themes: string;
  opinion: string;
  recommendation: string;
  /** "" or "1"–"5". */
  rating: string;
}

export interface BookReportLabels {
  defaultTitle: string;
  titleWithBook: string;
  byline: string;
  headings: {
    aboutBook: string;
    whereWhen: string;
    whoIs: string;
    whatHappens: string;
    bigIdea: string;
    favoritePart: string;
    whatIThink: string;
    recommend: string;
    introduction: string;
    settingCharacters: string;
    plotSummary: string;
    theme: string;
    opinion: string;
    recommendationConclusion: string;
    summary: string;
    analysis: string;
    evaluation: string;
    conclusion: string;
    workCited: string;
  };
  subheadings: {
    mainCharacters: string;
    setting: string;
    characters: string;
    themes: string;
    craft: string;
  };
  fields: {
    title: string;
    author: string;
    genre: string;
    published: string;
    rating: string;
  };
  ratingValue: string;
  guidance: { plot: string; summary: string; workCited: string };
  prompts: {
    setting: string;
    charactersElementary: string;
    plotElementary: string;
    themeElementary: string;
    favoritePart: string;
    opinionElementary: string;
    recommendElementary: string;
    hook: string;
    setting2: string;
    characters: string;
    plot: string;
    themes: string;
    themeExample: string;
    opinion: string;
    recommendation: string;
    closing: string;
    context: string;
    thesis: string;
    plotHigh: string;
    charactersHigh: string;
    themesHigh: string;
    evidence: string;
    craft: string;
    evaluation: string;
    conclusionHigh: string;
    workCited: string;
  };
  authorFallback: string;
}

const AUTHOR_SEPARATOR = /\s*(?:;|&|\band\b)\s*/i;
const STAR = "★";
const EMPTY_STAR = "☆";

/** "Kate DiCamillo and Harry Bliss" → two names; "Harper Lee" → one. */
export function parseAuthors(raw: string): PersonName[] {
  return raw
    .split(AUTHOR_SEPARATOR)
    .map((name) => parsePersonName(name))
    .filter((name): name is PersonName => name !== null);
}

/**
 * The MLA 9 Works Cited entry for a print book: "Lee, Harper. *To Kill a
 * Mockingbird*. J. B. Lippincott, 1960." Null without an author and a title. A title
 * ending in "?" or "!" keeps its own mark instead of taking a period.
 */
export function mlaBookEntry(
  author: string,
  title: string,
  publisher: string,
  year: string
): DocRun[] | null {
  const names = parseAuthors(author);
  const bookTitle = title.trim();
  if (names.length === 0 || bookTitle === "") {
    return null;
  }
  const titleClose = withPeriod(bookTitle).slice(bookTitle.length);
  const publication = [publisher.trim(), year.trim()]
    .filter((part) => part !== "")
    .join(", ");
  return [
    { text: `${withPeriod(mlaAuthors(names))} ` },
    { text: bookTitle, italic: true },
    {
      text: publication
        ? `${titleClose} ${withPeriod(publication)}`
        : titleClose,
    },
  ];
}

/** "★★★★☆" for elementary readers; null when no rating was given. */
export function ratingStars(raw: string): string | null {
  const rating = Number.parseInt(raw, 10);
  if (!(rating >= 1 && rating <= 5)) {
    return null;
  }
  return STAR.repeat(rating) + EMPTY_STAR.repeat(5 - rating);
}

function charactersBlocks(text: string, prompt: string): DocBlock[] {
  const lines = splitItems(text);
  if (lines.length === 0) {
    return [{ kind: "prompt", text: prompt }];
  }
  return [
    {
      kind: "list",
      list: {
        style: "bullet",
        items: lines.map((line) => ({ text: leadInItem(line) })),
      },
    },
  ];
}

function bookFields(
  input: BookReportInput,
  labels: BookReportLabels,
  withYear: boolean
): DocBlock {
  const title = input.title.trim();
  const fields: DocField[] = [
    {
      label: labels.fields.title,
      value: title ? [{ text: title, italic: true }] : undefined,
    },
    { label: labels.fields.author, value: input.author.trim() },
    { label: labels.fields.genre, value: input.genre.trim() },
  ];
  if (withYear) {
    fields.push({ label: labels.fields.published, value: input.year.trim() });
  }
  return { kind: "fields", fields };
}

function ratingBlocks(
  input: BookReportInput,
  labels: BookReportLabels
): DocBlock[] {
  const stars = ratingStars(input.rating);
  if (!stars) {
    return [];
  }
  const value =
    input.level === "elementary"
      ? stars
      : `${stars} ${t(labels.ratingValue, { rating: Number(input.rating) })}`;
  return [{ kind: "fields", fields: [{ label: labels.fields.rating, value }] }];
}

function elementary(
  input: BookReportInput,
  labels: BookReportLabels
): DocSection[] {
  const { headings, prompts } = labels;
  return [
    { heading: headings.aboutBook, blocks: [bookFields(input, labels, false)] },
    {
      heading: headings.whereWhen,
      blocks: paragraphsOrPrompt(input.setting, prompts.setting),
    },
    {
      heading: headings.whoIs,
      blocks: charactersBlocks(input.characters, prompts.charactersElementary),
    },
    {
      heading: headings.whatHappens,
      blocks: paragraphsOrPrompt(input.plot, prompts.plotElementary),
    },
    {
      heading: headings.bigIdea,
      blocks: listOrPrompt(input.themes, "bullet", prompts.themeElementary),
    },
    {
      heading: headings.favoritePart,
      blocks: [{ kind: "prompt", text: prompts.favoritePart }],
    },
    {
      heading: headings.whatIThink,
      blocks: [
        ...ratingBlocks(input, labels),
        ...paragraphsOrPrompt(input.opinion, prompts.opinionElementary),
      ],
    },
    {
      heading: headings.recommend,
      blocks: paragraphsOrPrompt(
        input.recommendation,
        prompts.recommendElementary
      ),
    },
  ];
}

function middle(
  input: BookReportInput,
  labels: BookReportLabels
): DocSection[] {
  const { headings, prompts, subheadings } = labels;
  return [
    {
      heading: headings.introduction,
      blocks: [
        bookFields(input, labels, true),
        { kind: "prompt", text: prompts.hook },
      ],
    },
    {
      heading: headings.settingCharacters,
      blocks: [
        ...paragraphsOrPrompt(input.setting, prompts.setting2),
        { kind: "subheading", text: subheadings.mainCharacters },
        ...charactersBlocks(input.characters, prompts.characters),
      ],
    },
    {
      heading: headings.plotSummary,
      blocks: [
        { kind: "guidance", text: labels.guidance.plot },
        ...paragraphsOrPrompt(input.plot, prompts.plot),
      ],
    },
    {
      heading: headings.theme,
      blocks: [
        ...listOrPrompt(input.themes, "bullet", prompts.themes),
        { kind: "prompt", text: prompts.themeExample },
      ],
    },
    {
      heading: headings.opinion,
      blocks: [
        ...ratingBlocks(input, labels),
        ...paragraphsOrPrompt(input.opinion, prompts.opinion),
      ],
    },
    {
      heading: headings.recommendationConclusion,
      blocks: [
        ...paragraphsOrPrompt(input.recommendation, prompts.recommendation),
        { kind: "prompt", text: prompts.closing },
      ],
    },
  ];
}

function high(input: BookReportInput, labels: BookReportLabels): DocSection[] {
  const { headings, prompts, subheadings } = labels;
  const surname = parseAuthors(input.author).map(familyName)[0];
  const entry = mlaBookEntry(
    input.author,
    input.title,
    input.publisher,
    input.year
  );
  return [
    {
      heading: headings.introduction,
      blocks: [
        bookFields(input, labels, true),
        { kind: "prompt", text: prompts.context },
        { kind: "prompt", text: prompts.thesis },
      ],
    },
    {
      heading: headings.summary,
      blocks: [
        { kind: "guidance", text: labels.guidance.summary },
        ...paragraphsOrPrompt(input.plot, prompts.plotHigh),
      ],
    },
    {
      heading: headings.analysis,
      blocks: [
        { kind: "subheading", text: subheadings.setting },
        ...paragraphsOrPrompt(input.setting, prompts.setting2),
        { kind: "subheading", text: subheadings.characters },
        ...charactersBlocks(input.characters, prompts.charactersHigh),
        { kind: "subheading", text: subheadings.themes },
        ...listOrPrompt(input.themes, "bullet", prompts.themesHigh),
        {
          kind: "prompt",
          text: t(prompts.evidence, {
            surname: surname ?? labels.authorFallback,
          }),
        },
        { kind: "subheading", text: subheadings.craft },
        { kind: "prompt", text: prompts.craft },
      ],
    },
    {
      heading: headings.evaluation,
      blocks: [
        ...ratingBlocks(input, labels),
        ...paragraphsOrPrompt(input.opinion, prompts.evaluation),
      ],
    },
    {
      heading: headings.conclusion,
      blocks: [
        ...splitItems(input.recommendation).map(
          (text): DocBlock => ({ kind: "paragraph", text })
        ),
        { kind: "prompt", text: prompts.conclusionHigh },
      ],
    },
    {
      heading: headings.workCited,
      blocks: entry
        ? [
            { kind: "paragraph", text: entry },
            { kind: "guidance", text: labels.guidance.workCited },
          ]
        : [{ kind: "prompt", text: prompts.workCited }],
    },
  ];
}

const BUILDERS: Record<
  BookReportLevel,
  (input: BookReportInput, labels: BookReportLabels) => DocSection[]
> = { elementary, middle, high };

export function buildBookReport(
  input: BookReportInput,
  labels: BookReportLabels
): DocumentModel {
  const title = input.title.trim();
  const student = input.student.trim();
  return {
    title: title
      ? interpolateRuns(labels.titleWithBook, { title }, ["title"])
      : labels.defaultTitle,
    subtitle: student ? t(labels.byline, { name: student }) : undefined,
    sections: BUILDERS[input.level](input, labels),
  };
}
